import React, { useMemo } from 'react';
import { useSceneViewStore } from '../../store/sceneViewStore';

interface SceneGridProps {
  width: number;
  height: number;
}

export const SceneGrid: React.FC<SceneGridProps> = ({ width, height }) => {
  const { viewport } = useSceneViewStore();
  const { zoom, panX, panY, gridSize, showGrid } = viewport;

  const gridLines = useMemo(() => {
    if (!showGrid || width <= 0 || height <= 0) {
      return { verticalLines: [], horizontalLines: [] };
    }

    const scaledGridSize = gridSize * zoom;
    const minGridSize = 10; // Minimum pixel distance between lines

    // Skip rendering if grid is too dense
    if (scaledGridSize < minGridSize) {
      return { verticalLines: [], horizontalLines: [] };
    }

    // Calculate offset from panning
    const offsetX = (panX * zoom) % scaledGridSize;
    const offsetY = (panY * zoom) % scaledGridSize;

    const verticalLines: number[] = [];
    const horizontalLines: number[] = [];

    // Generate vertical lines
    for (let x = offsetX; x <= width; x += scaledGridSize) {
      verticalLines.push(x);
    }
    // Add lines before the offset too
    for (let x = offsetX - scaledGridSize; x >= 0; x -= scaledGridSize) {
      verticalLines.unshift(x);
    }

    // Generate horizontal lines
    for (let y = offsetY; y <= height; y += scaledGridSize) {
      horizontalLines.push(y);
    }
    // Add lines before the offset too
    for (let y = offsetY - scaledGridSize; y >= 0; y -= scaledGridSize) {
      horizontalLines.unshift(y);
    }

    return { verticalLines, horizontalLines };
  }, [width, height, zoom, panX, panY, gridSize, showGrid]);

  if (!showGrid) {
    return null;
  }

  const { verticalLines, horizontalLines } = gridLines;

  // Calculate origin position
  const originX = (width / 2) + (panX * zoom);
  const originY = (height / 2) + (panY * zoom);

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
    >
      {/* Minor grid lines */}
      <g stroke="rgba(255, 255, 255, 0.05)" strokeWidth="1">
        {verticalLines.map((x, i) => (
          <line key={`v-${i}`} x1={x} y1={0} x2={x} y2={height} />
        ))}
        {horizontalLines.map((y, i) => (
          <line key={`h-${i}`} x1={0} y1={y} x2={width} y2={y} />
        ))}
      </g>

      {/* Origin axes (X = red, Y = green) */}
      {originX >= 0 && originX <= width && (
        <line
          x1={originX}
          y1={0}
          x2={originX}
          y2={height}
          stroke="rgba(74, 222, 128, 0.4)"
          strokeWidth="1"
        />
      )}
      {originY >= 0 && originY <= height && (
        <line
          x1={0}
          y1={originY}
          x2={width}
          y2={originY}
          stroke="rgba(248, 113, 113, 0.4)"
          strokeWidth="1"
        />
      )}

      {/* Origin point */}
      {originX >= 0 && originX <= width && originY >= 0 && originY <= height && (
        <circle
          cx={originX}
          cy={originY}
          r={4}
          fill="rgba(250, 204, 21, 0.6)"
          stroke="rgba(250, 204, 21, 0.8)"
          strokeWidth="1"
        />
      )}
    </svg>
  );
};
